// 2P AI — configurazione progetto (§8.5), 1:1 con 2pai-progetto-config.html. id="new" = creazione.
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useProjects, useServers } from '../lib/queries';
import { onCardKey } from '../lib/a11y';
import { CommandBar } from '../components/CommandBar';
import { Crumb } from '../components/Crumb';
import { Icon } from '../components/Icon';
import { TagList } from '../components/TagList';

interface ModelInfo { id: string; display_name?: string }
type Autonomy = 'read' | 'gate' | 'auto';

const KINDS = ['Next.js', 'WordPress', 'WooCommerce', 'Server / infrastruttura', 'Altro'];

export function ProjectConfig({ id }: { id: string }) {
  const isNew = id === 'new';
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const projects = useProjects().data ?? [];
  const servers = useServers().data ?? [];
  const models = useQuery({ queryKey: ['models'], queryFn: () => api.get<{ items: ModelInfo[] }>('/models').then((d) => d.items), retry: false }).data ?? [];
  const project = isNew ? undefined : projects.find((p) => p.id === id);

  const [name, setName] = useState('');
  const [kind, setKind] = useState(KINDS[0]);
  const [serverId, setServerId] = useState('');
  const [workingDir, setWorkingDir] = useState('');
  const [gitRepo, setGitRepo] = useState('');
  const [autonomy, setAutonomy] = useState<Autonomy>('gate');
  const [protectedPaths, setProtectedPaths] = useState<string[]>([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [notify, setNotify] = useState({ awaiting: true, done: true, error: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Radici consentite del server scelto (deve stare DOPO gli useState: legge serverId).
  // Prima era sopra le dichiarazioni → la callback di .find() girava con serverId ancora nella
  // temporal dead zone e crashava la schermata appena la lista server non era vuota.
  const selectedRoots = servers.find((s) => s.id === serverId)?.working_roots ?? [];

  // Create: auto-seleziona il primo server e pre-compila la cartella con la sua prima radice consentita
  // (così il percorso di default e' gia' DENTRO l'allowlist → niente "Validation failed" a sorpresa).
  useEffect(() => {
    if (isNew && !serverId && servers.length > 0) {
      const s0 = servers[0];
      setServerId(s0.id);
      setWorkingDir((r) => r || (s0.working_roots ?? [])[0] || '');
    }
  }, [isNew, servers, serverId]);

  // Cambiando server in creazione, riporta la cartella nella prima radice consentita del nuovo server.
  function pickServer(sid: string) {
    setServerId(sid);
    if (isNew) setWorkingDir((servers.find((s) => s.id === sid)?.working_roots ?? [])[0] ?? '');
  }

  // Edit: prefill una sola volta, quando il progetto è disponibile.
  useEffect(() => {
    if (isNew || loaded || !project) return;
    setName(project.name);
    setKind(project.kind || KINDS[0]);
    setServerId(project.server_id);
    setWorkingDir(project.working_dir);
    setGitRepo(project.git_repo || '');
    setAutonomy((['read', 'gate', 'auto'].includes(project.default_autonomy ?? '') ? project.default_autonomy : 'gate') as Autonomy);
    setProtectedPaths(project.protected_paths ?? []);
    setDefaultModel(project.default_model ?? '');
    setNotify({ awaiting: project.notify_awaiting ?? true, done: project.notify_done ?? true, error: project.notify_error ?? true });
    setLoaded(true);
  }, [isNew, project, loaded]);

  async function save() {
    setError(null);
    if (!name.trim() || !serverId || !workingDir.trim()) { setError('Nome, server e cartella di lavoro sono obbligatori.'); return; }
    // Controllo allowlist lato client: messaggio chiaro subito, invece del generico "Validation failed" dal server.
    const wd = (workingDir.trim().replace(/\/+$/, '') || '/');
    const inside = selectedRoots.some((r) => { const rr = r.replace(/\/+$/, '') || '/'; return wd === rr || wd.startsWith(rr + '/'); });
    if (selectedRoots.length > 0 && !inside) {
      setError(`La cartella di lavoro dev'essere dentro le cartelle consentite di questo server: ${selectedRoots.join(', ')}.`);
      return;
    }
    setBusy(true);
    const body = {
      name: name.trim(),
      server_id: serverId,
      working_dir: workingDir.trim(),
      kind,
      git_repo: gitRepo.trim() || null,
      default_autonomy: autonomy,
      default_model: defaultModel,
      protected_paths: protectedPaths,
      notify_awaiting: notify.awaiting,
      notify_done: notify.done,
      notify_error: notify.error,
    };
    try {
      let newId = id;
      if (isNew) {
        const res = await api.post<{ project: { id: string } }>('/projects', body);
        newId = res.project.id;
      } else {
        await api.patch<unknown>(`/projects/${id}`, body);
      }
      void qc.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/p/${newId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('Rimuovere il progetto da 2P AI? Cancella config e storico, non tocca i file.')) return;
    setBusy(true);
    try {
      await api.del(`/projects/${id}`);
      void qc.invalidateQueries({ queryKey: ['projects'] });
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile rimuovere');
      setBusy(false);
    }
  }

  const opt = (k: Autonomy, t: string, d: string, rec?: boolean) => (
    <div className={autonomy === k ? 'opt sel' : 'opt'} role="button" tabIndex={0} onClick={() => setAutonomy(k)} onKeyDown={onCardKey(() => setAutonomy(k))}>
      <div className="rad" />
      <div><div className="ot">{t}{rec ? <span style={{ color: 'var(--accent)' }}> · consigliato</span> : null}</div><div className="od">{d}</div></div>
    </div>
  );
  const sw = (on: boolean, toggle: () => void, label: string) => (
    <button type="button" className={on ? 'switch on' : 'switch'} role="switch" aria-checked={on} aria-label={label} onClick={toggle} />
  );

  return (
    <div className="app">
      <CommandBar />
      <div className="form">
        <Crumb current={isNew ? 'Nuovo progetto' : 'Configurazione'} onBack={() => navigate(isNew ? '/' : `/p/${id}`)} backLabel={isNew ? 'Progetti' : (project?.name ?? 'Progetto')} />
        <div className="ftitle">{isNew ? 'Nuovo progetto' : 'Configurazione progetto'}</div>
        <div className="fsub">Come è collocato e quanta libertà ha Claude Code qui dentro</div>

        <div className="fsection">
          <h3><Icon name="badge" />Identità</h3>
          <div className="field"><label>Nome del progetto</label><input className="inp" type="text" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="field"><label>Tipo</label>
            <div className="selwrap"><select className="sel" value={kind} onChange={(e) => setKind(e.target.value)}>{KINDS.map((k) => <option key={k}>{k}</option>)}</select><Icon name="expand_more" /></div>
          </div>
        </div>

        <div className="fsection">
          <h3><Icon name="dns" />Posizione</h3>
          <div className="field"><label>Server</label>
            <div className="selwrap"><select className="sel" value={serverId} disabled={servers.length === 0} onChange={(e) => pickServer(e.target.value)}>{servers.length === 0 ? <option value="">nessun server — creane uno prima</option> : null}{servers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select><Icon name="expand_more" /></div>
            <div className="help">La macchina su cui gira il progetto e dove Claude Code lavorerà.</div>
          </div>
          <div className="field"><label>Cartella di lavoro</label><input className="inp mono" type="text" value={workingDir} onChange={(e) => setWorkingDir(e.target.value)} placeholder="/var/www/…" />
            <div className="help">{selectedRoots.length > 0
              ? <>Dev'essere dentro le cartelle consentite di questo server: <span className="mono" style={{ color: 'var(--text-mid)' }}>{selectedRoots.join('  ·  ')}</span></>
              : 'Questo server non ha cartelle consentite: impostale nella sua configurazione prima di creare un progetto.'}</div>
          </div>
          <div className="field"><label>Repository git <span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>· facoltativo</span></label><input className="inp mono" type="text" value={gitRepo} onChange={(e) => setGitRepo(e.target.value)} placeholder="git@github.com:2pweb/…" /></div>
        </div>

        <div className="fsection">
          <h3><Icon name="shield" />Autonomia di Claude Code</h3>
          <div className="field"><label>Quanto può fare da solo su questo progetto</label>
            <div className="choice">
              {opt('read', 'Solo lettura', 'Legge, analizza e propone. Non tocca nulla: ogni intervento lo apri e lo guidi tu.')}
              {opt('gate', 'Modifiche con conferma', 'Lavora e prepara le modifiche, ma ognuna passa dal tuo OK prima di essere applicata.', true)}
              {opt('auto', 'Autonomo', 'Applica le modifiche da solo, tranne nelle aree protette qui sotto.')}
            </div>
          </div>
          <div className="field"><label>Aree protette</label>
            <TagList items={protectedPaths} promptText="File o cartella (es. .env)" onAdd={(v) => setProtectedPaths((p) => p.includes(v) ? p : [...p, v])} onRemove={(v) => setProtectedPaths((p) => p.filter((x) => x !== v))} />
            <div className="help">File e cartelle che chiedono sempre il tuo OK, anche in modalità autonoma.</div>
          </div>
        </div>

        <div className="fsection">
          <h3><Icon name="neurology" />Modello predefinito</h3>
          <div className="field"><label>Quale modello usare quando apri una sessione qui</label>
            <div className="selwrap"><select className="sel" value={defaultModel} onChange={(e) => setDefaultModel(e.target.value)}>
              <option value="">Il più recente (consigliato)</option>
              <option value="strongest">Sempre il più potente</option>
              {models.map((m) => <option key={m.id} value={m.id}>{m.display_name ?? m.id}</option>)}
            </select><Icon name="expand_more" /></div>
            <div className="help">Resta sempre aggiornato da solo: quando esce un modello nuovo, parte da quello. Puoi cambiarlo al volo in ogni sessione.</div>
          </div>
        </div>

        <div className="fsection">
          <h3><Icon name="notifications" />Notifiche di questo progetto</h3>
          <div className="row-toggle"><div><div className="tl">Quando aspetta il tuo OK</div><div className="td">Una sessione si ferma e chiede via libera</div></div>{sw(notify.awaiting, () => setNotify((n) => ({ ...n, awaiting: !n.awaiting })), 'Notifica attende OK')}</div>
          <div className="row-toggle"><div><div className="tl">Quando completa un lavoro</div><div className="td">Una sessione finisce con successo</div></div>{sw(notify.done, () => setNotify((n) => ({ ...n, done: !n.done })), 'Notifica completata')}</div>
          <div className="row-toggle"><div><div className="tl">Quando va in errore</div><div className="td">Una sessione si interrompe per un problema</div></div>{sw(notify.error, () => setNotify((n) => ({ ...n, error: !n.error })), 'Notifica errore')}</div>
        </div>

        {!isNew ? (
          <div className="danger">
            <h3>Zona delicata</h3>
            <p>Rimuovere il progetto cancella la sua configurazione e lo storico delle sessioni da 2P AI. Non tocca i file sul server.</p>
            <button className="btn-danger" type="button" disabled={busy} onClick={() => void remove()}><Icon name="delete" />Rimuovi progetto</button>
          </div>
        ) : null}

        {error ? <div className="note danger"><Icon name="error" /><span>{error}</span></div> : null}

        <div className="actionbar">
          <button className="btn-ghost" type="button" onClick={() => navigate(isNew ? '/' : `/p/${id}`)}>Annulla</button>
          <button className="btn-primary" type="button" disabled={busy} onClick={() => void save()}><Icon name="check" />{busy ? '…' : 'Salva'}</button>
        </div>
      </div>
    </div>
  );
}
